import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddShoppingCartOutlined as AddItemsIcon,
  Close as CloseIcon,
  EditOutlined as EditIcon,
  LinkOutlined as LinkIcon,
  LinkOffOutlined as UnlinkIcon,
  RemoveCircleOutline as VoidIcon,
} from '@mui/icons-material';
import TablesService from '../../services/TablesService';
import SnackbarUtils from '../../utils/SnackbarUtils';
import BillingModal from '../location/sales/billing_modal/BillingModal';
import ProductPickerDialog from './ProductPickerDialog';
import PickTableDialog from './PickTableDialog';
import WaiterSelect from './WaiterSelect';
import { describeTablesError, elapsedLabel, groupByRound, money, toBillingItems } from './tablesUtils';

const STATUS_CHIPS = {
  CHARGING: { label: 'Cobrando', color: 'info' },
  CHARGED: { label: 'Cobrado', color: 'success' },
  VOID: { label: 'Anulado', color: 'default' },
};

const TabPanel = ({ tabId, areas, locationId, perms, onClose, onChanged }) => {
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picking, setPicking] = useState(null); // 'move' | 'items' | 'link'
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ guests: '', waiterEmail: '' });
  const [billingOpen, setBillingOpen] = useState(false);
  // El BillingModal consulta el POS de la terminal apenas se monta (aunque esté cerrado): solo se monta al cobrar.
  const [billingMounted, setBillingMounted] = useState(false);
  const chargeContext = useRef(null);

  const apply = useCallback(
    (next) => {
      setTab(next);
      setSelected((previous) => previous.filter((id) => next.items.some((item) => item.id === id && item.status === 'ACTIVE')));
      onChanged();
    },
    [onChanged]
  );

  const load = useCallback(async () => {
    try {
      setTab(await TablesService.getTab(tabId));
    } catch (error) {
      SnackbarUtils.error(describeTablesError(error, 'No pudimos cargar la cuenta'));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [tabId, onClose]);

  useEffect(() => {
    setLoading(true);
    setSelected([]);
    load();
  }, [load]);

  const run = async (action, successMessage) => {
    setBusy(true);
    try {
      const result = await action();
      if (successMessage) SnackbarUtils.success(successMessage);
      return { ok: true, result };
    } catch (error) {
      SnackbarUtils.error(describeTablesError(error));
      // Si la cuenta cambió por otro lado (otro mozo, otra caja), se refresca para no operar con datos viejos.
      load();
      return { ok: false };
    } finally {
      setBusy(false);
    }
  };

  const activeItems = useMemo(() => (tab ? tab.items.filter((item) => item.status === 'ACTIVE') : []), [tab]);
  const isOpen = tab?.status === 'OPEN';
  const rounds = useMemo(() => (tab ? groupByRound(tab.items) : []), [tab]);
  const chargeItems = selected.length > 0 ? activeItems.filter((item) => selected.includes(item.id)) : activeItems;
  const chargeAmount = chargeItems.reduce((sum, item) => sum + item.subtotal, 0);

  const toggle = (id) => setSelected((previous) => (previous.includes(id) ? previous.filter((x) => x !== id) : [...previous, id]));

  const addItems = async (items) => {
    const { ok, result } = await run(() => TablesService.addItems(tab.id, items), 'Pedido enviado a la mesa');
    if (ok) {
      setPickerOpen(false);
      apply(result);
    }
  };

  const confirmVoid = async () => {
    const target = voidTarget;
    setVoidTarget(null);
    const { ok, result } = await run(() => TablesService.voidItem(tab.id, target.id, voidReason.trim() || null), 'Ítem anulado');
    if (ok) apply(result);
    setVoidReason('');
  };

  const closeTab = async () => {
    const { ok } = await run(() => TablesService.closeTab(tab.id), 'Mesa liberada');
    if (ok) {
      onChanged();
      onClose();
    }
  };

  const saveEdit = async () => {
    const { ok, result } = await run(() =>
      TablesService.updateTab(tab.id, {
        guests: editForm.guests === '' ? null : Number(editForm.guests),
        waiterEmail: editForm.waiterEmail.trim() || null,
      })
    );
    if (ok) {
      setEditOpen(false);
      apply(result);
    }
  };

  const pickTable = async (table) => {
    const mode = picking;
    setPicking(null);
    if (mode === 'move') {
      const { ok, result } = await run(() => TablesService.moveTab(tab.id, table.id), `Cuenta movida a ${table.name}`);
      if (ok) apply(result);
    } else if (mode === 'link') {
      const { ok, result } = await run(() => TablesService.linkTable(tab.id, table.id), `Mesa ${table.name} unida`);
      if (ok) apply(result);
    } else if (mode === 'items') {
      const { ok, result } = await run(
        () => TablesService.moveItems(tab.id, selected, table.id),
        `Ítems pasados a ${table.name}`
      );
      if (ok) {
        setSelected([]);
        if (result.sourceClosed) {
          onChanged();
          onClose();
        } else {
          apply(result.source);
        }
      }
    }
  };

  const unlink = async (tableId) => {
    const { ok, result } = await run(() => TablesService.unlinkTable(tab.id, tableId), 'Mesa separada');
    if (ok) apply(result);
  };

  // ---- Cobro: el pago lo hace el flujo del POS (BillingModal) con los ítems elegidos ----
  const startCharge = () => {
    chargeContext.current = { itemIds: chargeItems.map((item) => item.id), tab: null };
    setBillingMounted(true);
    setBillingOpen(true);
  };

  const createSale = async (saleData) => {
    const { itemIds } = chargeContext.current;
    try {
      const result = await TablesService.charge(tab.id, {
        itemIds,
        terminalCode: saleData.terminal_code,
        paymentMethod: saleData.purchase.payment_method,
        paymentCategoryId: saleData.purchase.payment_category_id,
        paymentSubcategoryId: saleData.purchase.payment_subcategory_id,
        paymentTransactionId: saleData.payment_transaction_id,
        paymentCurrency: saleData.payment_currency,
        payments: saleData.payments,
        client: saleData.client,
        company: saleData.company,
        adenda: saleData.adenda,
      });
      chargeContext.current = { ...chargeContext.current, tab: result.tab };
      return result.invoice;
    } catch (error) {
      // El BillingModal muestra error_message tal cual: se le da el texto ya traducido.
      const friendly = describeTablesError(error, 'No se pudo cobrar la mesa');
      error.response = { ...(error.response || {}), data: { ...(error.response?.data || {}), error_message: friendly } };
      load();
      throw error;
    }
  };

  const finishCharge = () => {
    const paidTab = chargeContext.current?.tab;
    chargeContext.current = null;
    setBillingOpen(false);
    SnackbarUtils.success('Cobro registrado');
    onChanged();
    if (!paidTab || paidTab.status !== 'OPEN') {
      onClose();
    } else {
      apply(paidTab);
    }
  };

  const freeTableIds = [tab?.tableId, ...(tab?.linkedTableIds || [])].filter(Boolean);
  const width = { xs: '100vw', sm: 460 };

  return (
    // 1250: por encima del AppBar (1201) y por debajo de los diálogos (1300) que se abren desde el panel.
    <Drawer anchor="right" open onClose={onClose} sx={{ zIndex: 1250 }} PaperProps={{ sx: { width } }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {tab ? `Mesa ${tab.tableName}` : 'Cuenta'}
          </Typography>
          {tab && isOpen && (
            <Tooltip title="Editar comensales y mozo">
              <IconButton
                onClick={() => {
                  setEditForm({ guests: tab.guests ?? '', waiterEmail: tab.waiterEmail || '' });
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        {loading || !tab ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ my: 1 }}>
              <Chip size="small" label={`Abierta hace ${elapsedLabel(tab.openedAt)}`} />
              {tab.guests != null && <Chip size="small" label={`${tab.guests} comensales`} />}
              {tab.waiterEmail && <Chip size="small" label={tab.waiterName || tab.waiterEmail} />}
              {tab.status !== 'OPEN' && <Chip size="small" color="success" label="Cuenta cerrada" />}
              {tab.linkedTableIds.map((id) => {
                const linked = areas.flatMap((area) => area.tables).find((table) => table.id === id);
                return (
                  <Chip
                    key={id}
                    size="small"
                    icon={<LinkIcon />}
                    label={linked?.name || id}
                    onDelete={isOpen && perms.operate ? () => unlink(id) : undefined}
                    deleteIcon={<UnlinkIcon />}
                  />
                );
              })}
            </Stack>

            <Divider />

            <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
              {rounds.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  La cuenta no tiene pedidos todavía.
                </Typography>
              )}
              {rounds.map((round) => (
                <Box key={round.roundNo} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Pedido {round.roundNo}</Typography>
                  {round.items.map((item) => {
                    const chip = STATUS_CHIPS[item.status];
                    const inactive = item.status !== 'ACTIVE';
                    return (
                      <Stack key={item.id} direction="row" alignItems="center" spacing={0.5} sx={{ opacity: item.status === 'VOID' ? 0.5 : 1 }}>
                        {isOpen && perms.operate && (
                          <Checkbox size="small" disabled={inactive || busy} checked={selected.includes(item.id)} onChange={() => toggle(item.id)} />
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ textDecoration: item.status === 'VOID' ? 'line-through' : 'none' }}>
                            {item.quantity} × {item.name}
                          </Typography>
                          {item.notes && <Typography variant="caption" color="text.secondary">{item.notes}</Typography>}
                        </Box>
                        {chip && <Chip size="small" color={chip.color} label={chip.label} sx={{ height: 20 }} />}
                        <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'right' }}>{money(item.subtotal, tab.currency)}</Typography>
                        {isOpen && perms.void && item.status === 'ACTIVE' && (
                          <Tooltip title="Anular ítem">
                            <IconButton size="small" disabled={busy} onClick={() => setVoidTarget(item)}>
                              <VoidIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  })}
                </Box>
              ))}
            </Box>

            <Divider />
            <Box sx={{ py: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={700}>Pendiente</Typography>
                <Typography variant="subtitle1" fontWeight={700}>{money(tab.pendingTotal, tab.currency)}</Typography>
              </Stack>
              {tab.chargedTotal > 0 && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Ya cobrado</Typography>
                  <Typography variant="body2" color="text.secondary">{money(tab.chargedTotal, tab.currency)}</Typography>
                </Stack>
              )}
            </Box>

            {isOpen && (
              <Stack spacing={1}>
                {perms.operate && (
                  <Button variant="outlined" startIcon={<AddItemsIcon />} disabled={busy} onClick={() => setPickerOpen(true)}>
                    Agregar productos
                  </Button>
                )}
                {perms.charge && (
                  <Button variant="contained" size="large" disabled={busy || activeItems.length === 0} onClick={startCharge}>
                    {selected.length > 0 ? `Cobrar seleccionados (${money(chargeAmount, tab.currency)})` : `Cobrar todo (${money(chargeAmount, tab.currency)})`}
                  </Button>
                )}
                {perms.operate && (
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Button size="small" disabled={busy || selected.length === 0} onClick={() => setPicking('items')}>
                      Pasar ítems a…
                    </Button>
                    <Button size="small" disabled={busy} onClick={() => setPicking('move')}>Mover mesa</Button>
                    <Button size="small" disabled={busy} onClick={() => setPicking('link')}>Unir mesa</Button>
                    <Button size="small" color="inherit" disabled={busy || tab.items.some((i) => i.status === 'ACTIVE' || i.status === 'CHARGING')} onClick={closeTab}>
                      Cerrar mesa
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}
          </>
        )}
      </Box>

      {tab && (
        <>
          <ProductPickerDialog
            open={pickerOpen}
            locationId={locationId}
            lockedCurrency={tab.items.length > 0 ? tab.currency : null}
            submitting={busy}
            onClose={() => setPickerOpen(false)}
            onSubmit={addItems}
          />

          <PickTableDialog
            open={Boolean(picking)}
            title={picking === 'move' ? 'Mover la cuenta a otra mesa' : picking === 'link' ? 'Unir una mesa a la cuenta' : 'Pasar ítems a otra mesa'}
            description={
              picking === 'items'
                ? 'Si la mesa está libre se abre una cuenta nueva con los ítems; si está ocupada se suman a su cuenta.'
                : 'Solo se pueden elegir mesas libres.'
            }
            areas={areas}
            excludeTableIds={freeTableIds}
            allowOccupied={picking === 'items'}
            onClose={() => setPicking(null)}
            onPick={pickTable}
          />

          <Dialog open={Boolean(voidTarget)} onClose={() => setVoidTarget(null)} fullWidth maxWidth="xs">
            <DialogTitle>Anular {voidTarget?.name}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                label="Motivo (opcional)"
                value={voidReason}
                inputProps={{ maxLength: 255 }}
                onChange={(event) => setVoidReason(event.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setVoidTarget(null)}>Cancelar</Button>
              <Button color="error" variant="contained" onClick={confirmVoid}>Anular</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>Datos de la cuenta</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="Comensales"
                  type="number"
                  size="small"
                  value={editForm.guests}
                  inputProps={{ min: 1, max: 99 }}
                  onChange={(event) => setEditForm({ ...editForm, guests: event.target.value })}
                />
                <WaiterSelect
                  locationId={locationId}
                  value={editForm.waiterEmail}
                  onChange={(waiterEmail) => setEditForm({ ...editForm, waiterEmail })}
                  extraEmails={[localStorage.getItem('userEmail') || '']}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
              <Button variant="contained" disabled={busy} onClick={saveEdit}>Guardar</Button>
            </DialogActions>
          </Dialog>

          {billingMounted && (
          <BillingModal
            locationId={locationId}
            open={billingOpen}
            onClose={() => setBillingOpen(false)}
            items={toBillingItems(chargeItems, tab.currency)}
            discountCode={null}
            createSale={createSale}
            disableStoreCredit
            lockOrigin
            onFinish={finishCharge}
          />
          )}
        </>
      )}
    </Drawer>
  );
};

export default TabPanel;
